/* PawSculpt homepage interactions.
   Custom elements so each Shopify section boots independently in the theme editor. */

(function () {
  'use strict';

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  /* ------------------------------------------------------------------
     Material comparator — drag seam + material switch
     ------------------------------------------------------------------ */
  class PsCompare extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-compare-stage]');
      this.handle = this.querySelector('[data-compare-handle]');
      this.afterLayer = this.querySelector('[data-compare-after]');
      this.rightTag = this.querySelector('[data-compare-right-label]');
      this.switchButtons = Array.from(this.querySelectorAll('[data-material]'));
      this.cards = Array.from(this.querySelectorAll('[data-material-card]'));
      if (!this.stage || !this.handle) return;

      this.split = 50;
      this.dragging = false;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      this.stage.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      this.handle.addEventListener('keydown', this.onKeyDown);

      this.switchButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.selectMaterial(btn.dataset.material));
      });

      this.setSplit(50);
    }

    disconnectedCallback() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
    }

    setSplit(value) {
      this.split = clamp(value, 0, 100);
      this.stage.style.setProperty('--ps-split', this.split + '%');
      this.handle.setAttribute('aria-valuenow', Math.round(this.split));
      const label = this.handle.getAttribute('data-aria-template');
      if (label) {
        this.handle.setAttribute('aria-valuetext', label.replace('SPLIT', Math.round(this.split)));
      }
    }

    splitFromEvent(event) {
      const rect = this.stage.getBoundingClientRect();
      return ((event.clientX - rect.left) / rect.width) * 100;
    }

    onPointerDown(event) {
      this.dragging = true;
      this.stage.setPointerCapture?.(event.pointerId);
      this.setSplit(this.splitFromEvent(event));
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      event.preventDefault();
      this.setSplit(this.splitFromEvent(event));
    }

    onPointerUp() {
      this.dragging = false;
    }

    onKeyDown(event) {
      const step = event.shiftKey ? 10 : 2;
      if (event.key === 'ArrowLeft') {
        this.setSplit(this.split - step);
      } else if (event.key === 'ArrowRight') {
        this.setSplit(this.split + step);
      } else if (event.key === 'Home') {
        this.setSplit(0);
      } else if (event.key === 'End') {
        this.setSplit(100);
      } else {
        return;
      }
      event.preventDefault();
    }

    selectMaterial(key) {
      this.switchButtons.forEach((btn) => {
        btn.setAttribute('aria-selected', String(btn.dataset.material === key));
      });

      const active = this.switchButtons.find((btn) => btn.dataset.material === key);
      if (!active) return;

      const img = this.afterLayer?.querySelector('img');
      if (img && active.dataset.image) {
        img.src = active.dataset.image;
        if (active.dataset.srcset) img.srcset = active.dataset.srcset;
        img.alt = active.dataset.alt || img.alt;
      }
      if (this.rightTag && active.dataset.label) {
        this.rightTag.textContent = active.dataset.label;
      }

      this.cards.forEach((card) => {
        card.hidden = card.dataset.materialCard !== key && card.dataset.materialCard !== 'resin';
      });

      this.setSplit(50);
    }
  }

  /* ------------------------------------------------------------------
     Delivery timeline switch (US / International)
     ------------------------------------------------------------------ */
  class PsTimeline extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-region]'));
      this.detail = this.querySelector('[data-region-detail]');
      this.buttons.forEach((btn) => {
        btn.addEventListener('click', () => this.select(btn.dataset.region));
      });
    }

    select(region) {
      this.buttons.forEach((btn) => btn.setAttribute('aria-selected', String(btn.dataset.region === region)));
      const active = this.buttons.find((btn) => btn.dataset.region === region);
      if (active && this.detail && active.dataset.detail) {
        this.detail.innerHTML = active.dataset.detail;
      }
    }
  }

  /* ------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------ */
  function initReveal() {
    const items = document.querySelectorAll('.ps-reveal:not(.is-visible)');
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    items.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------------------
     Lazy-start the closing-band video only when it is on screen
     ------------------------------------------------------------------ */
  function initLazyVideo() {
    const videos = document.querySelectorAll('video[data-lazy-play]');
    if (!videos.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    videos.forEach((video) => observer.observe(video));
  }

  if (!customElements.get('ps-compare')) customElements.define('ps-compare', PsCompare);
  if (!customElements.get('ps-timeline')) customElements.define('ps-timeline', PsTimeline);

  function boot() {
    initReveal();
    initLazyVideo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders a section without a full page load.
  document.addEventListener('shopify:section:load', boot);
})();
